import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { getUser, getWeaveSessionId } from '@frontend/auth-helpers';
import { useComponentEngagementShallowStore } from '@frontend/high-value-adoption';
import { LocationCreditData } from '../types';

const WARNING_BANNER_COMPONENT_ID = 'air-credit-warning-banner';
const CRITICAL_BANNER_COMPONENT_ID = 'air-credit-critical-banner';

function getSource(userId: string, locationId: string): string {
  return `${userId}:${locationId}`;
}

export const useAIRCreditWarningBannerDismiss = <T extends LocationCreditData>(locationIds: T[]) => {
  const userId = getUser()?.userID ?? '';

  const { components, getEngagementData, trackDismiss } = useComponentEngagementShallowStore(
    'components',
    'getEngagementData',
    'trackDismiss'
  );

  const undismissedLocations = useMemo(() => {
    return locationIds.filter(({ locationId }) => {
      const data = getEngagementData({
        componentId: WARNING_BANNER_COMPONENT_ID,
        source: getSource(userId, locationId),
      });
      if (!data.dismissedExpiry) return true;
      return dayjs().isAfter(dayjs(data.dismissedExpiry));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationIds, userId, components, getEngagementData]);

  const dismissForLocations = useCallback(
    (locations: T[]) => {
      if (!userId) return;
      const defaultExpiry = dayjs().add(1, 'month').startOf('month').toISOString();
      locations.forEach(({ locationId, billingEndDate }) => {
        const expiryDate = billingEndDate ?? defaultExpiry;
        trackDismiss({ componentId: WARNING_BANNER_COMPONENT_ID, source: getSource(userId, locationId), expiryDate });
      });
    },
    [userId, trackDismiss]
  );

  return {
    undismissedLocations,
    dismissForLocations,
  };
};

export const useAIRCreditCriticalBannerDismiss = <T extends LocationCreditData>(locationIds: T[]) => {
  const userId = getUser()?.userID ?? '';
  const sessionId = getWeaveSessionId();

  const { components, getEngagementData, trackDismiss } = useComponentEngagementShallowStore(
    'components',
    'getEngagementData',
    'trackDismiss'
  );

  const undismissedLocations = useMemo(() => {
    return locationIds.filter(({ locationId }) => {
      const data = getEngagementData({
        componentId: CRITICAL_BANNER_COMPONENT_ID,
        source: getSource(userId, locationId),
      });
      return data.dismissedSessionId !== sessionId;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationIds, userId, sessionId, components, getEngagementData]);

  const dismissForLocations = useCallback(
    (locations: T[]) => {
      if (!userId) return;
      locations.forEach(({ locationId }) => {
        trackDismiss({
          componentId: CRITICAL_BANNER_COMPONENT_ID,
          source: getSource(userId, locationId),
          sessionId: sessionId ?? undefined,
        });
      });
    },
    [userId, sessionId, trackDismiss]
  );

  return {
    undismissedLocations,
    dismissForLocations,
  };
};
